// Ficha de una empresa: su gente (con llamar directo), oportunidades y toda la actividad. D95: franja de soporte
// (casos, horas del año, bolsa y Excel con todo lo del cliente).
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import * as crm from '../api/crm';
import type { Empresa } from '../api/types';
import { useEmpresa, useEtapas, useLlamadasEmpresa, useMiembros } from '../data/queries';
import { kpis } from '../data/selectors';
import { ago, duration, money, dueLabel } from '../lib/format';
import { Icon } from '../lib/icons';
import { formatPhone } from '../lib/phone-number';
import { Avatar, Empty, ErrorBox, IconDot, Kpi, Pill, Skeleton, Tel } from '../ui/bits';
import { abrirDialogo } from '../ui/forms';
import { NotaItem } from '../ui/Nota';
import { toast } from '../ui/toasts';
import { LicenciasEmpresa } from '../ui/LicenciasEmpresa';
import { Soporte360 } from './Soporte360';
import { SaldoEmpresa } from './ventas/Bolsas';

export function EmpresaFicha() {
  const { id = '' } = useParams();
  const e = useEmpresa(id);
  const nav = useNavigate();
  if (e.isLoading)
    return (
      <div className="view">
        <Skeleton rows={6} height={22} />
      </div>
    );
  if (e.error || !e.data)
    return (
      <div className="view">
        <ErrorBox error={e.error ?? new Error('No encontramos esta empresa.')} onRetry={() => e.refetch()} />
        <button className="btn" onClick={() => nav('/empresas')}>
          Volver a empresas
        </button>
      </div>
    );
  return <Ficha e={e.data} />;
}

function Ficha({ e }: { e: Empresa }) {
  const qc = useQueryClient();
  const personas = useQuery({ queryKey: ['colegas', e.id], queryFn: () => crm.personasDeEmpresa(e.id) });
  const opps = useQuery({ queryKey: ['oportunidades', 'empresa', e.id], queryFn: () => crm.oportunidadesDeEmpresa(e.id) });
  const notas = useQuery({ queryKey: ['timeline', 'notas-empresa', e.id], queryFn: () => crm.notasDe({ empresaId: e.id }) });
  const tareas = useQuery({ queryKey: ['timeline', 'tareas-empresa', e.id], queryFn: () => crm.tareasDeEmpresa(e.id) });
  const miembros = useMiembros();
  const responsable = e.responsableId ? miembros.data?.find((m) => m.id === e.responsableId)?.nombre : undefined;
  const tareasOrden = [...(tareas.data ?? [])].sort((a, b) => Number(a.estado === 'DONE') - Number(b.estado === 'DONE') || (a.vence ?? '9').localeCompare(b.vence ?? '9'));
  const llamadas = useLlamadasEmpresa(e.id);
  const etapas = useEtapas();
  const [nota, setNota] = useState<string | null>(null);
  const k = kpis(llamadas.data ?? []);
  const abierto = (opps.data ?? []).filter((o) => o.etapa !== etapas.data?.[etapas.data.length - 1]?.valor);

  const guardarNota = async () => {
    if (!nota?.trim()) return;
    try {
      await crm.crearNota({ texto: nota, empresaId: e.id });
      setNota(null);
      void qc.invalidateQueries({ queryKey: ['timeline', 'notas-empresa', e.id] });
      toast('Nota guardada.');
    } catch (err) {
      toast((err as Error).message, 'error');
    }
  };

  return (
    <div className="view">
      <div className="crumbs">
        <Link to="/empresas">Empresas</Link>
        <Icon name="chevRight" />
        <span>{e.nombre}</span>
      </div>
      <div className="fhead">
        <Avatar nombre={e.nombre} size="lg" square />
        <div className="who">
          <h1>{e.nombre}</h1>
          <div className="rl">
            {e.direccion && e.direccion !== e.ciudad ? <span>{e.direccion}</span> : e.ciudad ? <span>{e.ciudad}</span> : null}
            {responsable ? <span>Responsable: {responsable}</span> : null}
            <SaldoEmpresa empresaId={e.id} />
            {e.dominio ? (
              <a href={e.dominio.startsWith('http') ? e.dominio : `https://${e.dominio}`} target="_blank" rel="noreferrer">
                {e.dominio.replace(/^https?:\/\//, '')}
              </a>
            ) : null}
          </div>
        </div>
        <div className="vh-act">
          <button className="btn primary lg" onClick={() => abrirDialogo({ tipo: 'persona', empresaId: e.id })}>
            <Icon name="plus" />
            Contacto
          </button>
          <button className="btn lg" onClick={() => abrirDialogo({ tipo: 'oportunidad', empresaId: e.id })}>
            <Icon name="kanban" />
            Oportunidad
          </button>
          <button className="btn lg" onClick={() => setNota((v) => (v === null ? '' : null))}>
            <Icon name="note" />
            Nota
          </button>
          <button className="btn lg" onClick={() => abrirDialogo({ tipo: 'tarea', empresaId: e.id, nombre: e.nombre })}>
            <Icon name="task" />
            Tarea
          </button>
          <button className="btn lg icon" style={{ width: 40 }} onClick={() => abrirDialogo({ tipo: 'empresa', empresa: e })} aria-label="Editar empresa" title="Editar">
            <Icon name="edit" />
          </button>
        </div>
      </div>

      <section className="kpis">
        <Kpi label="Personas" value={personas.data?.length ?? '—'} />
        <Kpi label="Pipeline abierto" value={abierto.length ? money(abierto.reduce((a, o) => a + o.monto, 0), abierto[0].moneda) : '—'} detail={`${abierto.length} oportunidades`} />
        <Kpi label="Llamadas" value={llamadas.data?.length ?? '—'} detail={`${k.contestadas} contestadas`} />
        <Kpi label="Última llamada" value={llamadas.data?.[0] ? ago(llamadas.data[0].inicio) : '—'} />
      </section>

      <Soporte360 empresa={e} llamadas={llamadas.data ?? []} />

      {nota !== null ? (
        <div className="composer box">
          <label className="lbl" htmlFor="note-emp">
            Nota sobre {e.nombre}
          </label>
          <textarea id="note-emp" value={nota} onChange={(ev) => setNota(ev.target.value)} autoFocus />
          <div className="vh-act">
            <button className="btn primary" onClick={guardarNota} disabled={!nota.trim()}>
              Guardar nota
            </button>
            <button className="btn ghost" onClick={() => setNota(null)}>
              Cancelar
            </button>
          </div>
        </div>
      ) : null}

      <div className="cols2">
        <section className="box">
          <div className="box-h">
            <h2 className="lbl">Personas</h2>
          </div>
          {personas.isLoading ? (
            <Skeleton rows={4} />
          ) : personas.data?.length ? (
            <div className="rows">
              {personas.data.map((p) => (
                <div key={p.id} className="row act-row" style={{ gridTemplateColumns: '32px minmax(0,1fr) auto' }}>
                  <Avatar nombre={p.nombreCompleto} />
                  <Link to={`/personas/${p.id}`} style={{ minWidth: 0, textDecoration: 'none' }}>
                    <div className="nm">{p.nombreCompleto}</div>
                    <div className="sb">{p.cargo || p.email || '—'}</div>
                  </Link>
                  <Tel numero={p.telefono} contacto={{ id: p.id, nombre: p.nombreCompleto, empresa: e.nombre }} />
                </div>
              ))}
            </div>
          ) : (
            <Empty titulo="Sin personas registradas">
              <button className="btn" onClick={() => abrirDialogo({ tipo: 'persona', empresaId: e.id })}>
                <Icon name="plus" /> Agregar contacto
              </button>
            </Empty>
          )}
        </section>
        <div className="stackv">
          <section className="box">
            <div className="box-h">
              <h2 className="lbl">Oportunidades</h2>
              <Link className="btn ghost" to="/oportunidades">
                Ver pipeline
              </Link>
            </div>
            {opps.data?.length ? (
              <div className="rows">
                {opps.data.map((o) => (
                  <button key={o.id} className="row act-row tarea-abrir" onClick={() => abrirDialogo({ tipo: 'verOportunidad', id: o.id })} aria-label={`Abrir oportunidad: ${o.nombre}`}>
                    <IconDot name="kanban" tone="acc" />
                    <div style={{ minWidth: 0 }}>
                      <div className="nm">{o.nombre}</div>
                      <div className="sb">{etapas.data?.find((x) => x.valor === o.etapa)?.etiqueta ?? o.etapa}</div>
                    </div>
                    <span className="mono">{money(o.monto, o.moneda)}</span>
                  </button>
                ))}
              </div>
            ) : (
              <Empty titulo="Sin oportunidades" />
            )}
          </section>
          <section className="box">
            <div className="box-h">
              <h2 className="lbl">Tareas</h2>
              <button className="btn ghost" onClick={() => abrirDialogo({ tipo: 'tarea', empresaId: e.id, nombre: e.nombre })}>
                <Icon name="plus" /> Nueva
              </button>
            </div>
            {tareasOrden.length ? (
              <div className="rows">
                {tareasOrden.slice(0, 8).map((t) => (
                  <button key={t.id} className="row act-row tarea-abrir" onClick={() => abrirDialogo({ tipo: 'verTarea', id: t.id })} aria-label={`Abrir tarea: ${t.titulo}`}>
                    <IconDot name="task" tone={t.estado === 'DONE' ? 'ok' : 'warn'} />
                    <div style={{ minWidth: 0 }}>
                      <div className="nm" style={{ textDecoration: t.estado === 'DONE' ? 'line-through' : undefined }}>
                        {t.titulo}
                      </div>
                      <div className="sb">{t.estado === 'DONE' ? 'Hecha' : dueLabel(t.vence)}</div>
                    </div>
                    <span />
                  </button>
                ))}
              </div>
            ) : (
              <Empty titulo="Sin tareas" />
            )}
          </section>
          <LicenciasEmpresa empresaId={e.id} />
          <section className="box">
            <div className="box-h">
              <h2 className="lbl">Actividad</h2>
            </div>
            {(llamadas.data?.length ?? 0) + (notas.data?.length ?? 0) === 0 ? (
              <Empty titulo="Sin actividad todavía" />
            ) : (
              <div className="tl">
                {[
                  ...(notas.data ?? []).map((n) => ({ t: new Date(n.creado).getTime(), key: `n${n.id}`, node: <NotaItem n={n} onCambio={() => void qc.invalidateQueries({ queryKey: ['timeline', 'notas-empresa', e.id] })} /> })),
                  ...(llamadas.data ?? []).slice(0, 30).map((l) => ({ t: new Date(l.inicio ?? l.creado).getTime(), key: `l${l.id}`, node: (
                    <article className="tl-item">
                      <IconDot name={l.estado !== 'CONTESTADA' ? 'phoneMiss' : l.direccion === 'ENTRANTE' ? 'phoneIn' : 'phoneOut'} tone={l.estado !== 'CONTESTADA' ? 'bad' : 'acc'} />
                      <div style={{ minWidth: 0 }}>
                        <div className="tl-top">
                          <b>{l.persona?.nombre || formatPhone(l.numero)}</b>
                          {l.duracion ? <span className="mono" style={{ color: 'var(--muted)' }}>{duration(l.duracion)}</span> : null}
                          {l.tipificacion ? <Pill tone="acc">{l.tipificacion.nombre}</Pill> : null}
                          <time>{ago(l.inicio)}</time>
                        </div>
                      </div>
                    </article>
                  ) })),
                ]
                  .sort((a, b) => b.t - a.t)
                  .map((i) => (
                    <div key={i.key}>{i.node}</div>
                  ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
